import Autocomplete from "@mui/material/Autocomplete"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"

import Column from "../../../common/components/Column"
import { adminApi } from "../axios"
import AdminEntityAutocomplete from "./AdminEntityAutocomplete"

export default function AdminZohoMailboxEditDialog({
  open,
  mailbox,
  onClose,
  onSave,
  saving,
  errorMessage,
}) {
  const [form, setForm] = useState({
    assigned_user_id: "",
    ownerAccount: null,
  })
  const [concierge, setConcierge] = useState(null)
  const [accountOptions, setAccountOptions] = useState([])
  const [accountSearchLoading, setAccountSearchLoading] = useState(false)
  const [accountSearchQuery, setAccountSearchQuery] = useState("")

  useEffect(() => {
    if (!mailbox) return
    // Typeahead (active concierges via q) instead of a capped preload.
    setConcierge(
      mailbox.assigned_user_id
        ? {
            id: mailbox.assigned_user_id,
            name: mailbox.assigned_user_name,
            email: mailbox.assigned_user_email,
          }
        : null
    )
    setForm({
      assigned_user_id: mailbox.assigned_user_id ?? "",
      ownerAccount: mailbox.owner_user_id
        ? {
            id: mailbox.owner_user_id,
            email: mailbox.owner_user_email,
            name: mailbox.owner_user_name,
          }
        : null,
    })
    setAccountSearchQuery(
      mailbox.owner_user_email
        ? `${mailbox.owner_user_name || mailbox.owner_user_email} (${
            mailbox.owner_user_email
          })`
        : ""
    )
  }, [mailbox])

  useEffect(() => {
    if (!open) return undefined
    if (accountSearchQuery.length < 2) {
      setAccountOptions([])
      return undefined
    }
    const timer = setTimeout(() => {
      setAccountSearchLoading(true)
      adminApi
        .searchAccounts(accountSearchQuery)
        .then(({ data }) => setAccountOptions(data))
        .finally(() => setAccountSearchLoading(false))
    }, 300)
    return () => clearTimeout(timer)
  }, [open, accountSearchQuery])

  function handleSubmit(event) {
    event.preventDefault()
    onSave({
      assigned_user_id: Number(form.assigned_user_id),
      owner_user_id: Number(form.ownerAccount?.id),
    })
  }

  const canSave = form.assigned_user_id && form.ownerAccount?.id

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Reassign Zoho mailbox</DialogTitle>
        <DialogContent>
          <Column gap={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Mailbox: <strong>{mailbox?.email}</strong>
            </Typography>
            <AdminEntityAutocomplete
              entity="concierge"
              label="Concierge"
              placeholder="Search active concierges"
              value={concierge}
              onChange={(option) => {
                setConcierge(option)
                setForm((prev) => ({
                  ...prev,
                  assigned_user_id: option ? option.id : "",
                }))
              }}
            />
            <Autocomplete
              options={accountOptions}
              loading={accountSearchLoading}
              value={form.ownerAccount}
              inputValue={accountSearchQuery}
              onChange={(_, value) => {
                setForm((prev) => ({ ...prev, ownerAccount: value }))
                setAccountSearchQuery(
                  value ? `${value.name || value.email} (${value.email})` : ""
                )
              }}
              onInputChange={(_, value) => setAccountSearchQuery(value)}
              getOptionLabel={(option) =>
                option.email
                  ? `${option.name || option.email} (${option.email})`
                  : ""
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Parent account"
                  size="small"
                  helperText="Search by name or email (min. 2 characters)"
                  fullWidth
                  required
                />
              )}
            />
            <Typography variant="body2" color="text.secondary">
              Updates OneBase ownership only. The Zoho Mail user (email address)
              is unchanged. The concierge must have an active assignment on the
              parent account.
            </Typography>
            {errorMessage ? (
              <Typography color="error" variant="body2">
                {errorMessage}
              </Typography>
            ) : null}
          </Column>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || !canSave}
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

AdminZohoMailboxEditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mailbox: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  errorMessage: PropTypes.string,
}
